/**
 * MagicBI Design System - Header Component
 * 
 * Flexible header component for navigation and branding.
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

// Header variant types
type HeaderVariant = 'default' | 'sticky' | 'floating';
type HeaderSize = 'sm' | 'md' | 'lg';

// Header props interface
export interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  /** Header variant */
  variant?: HeaderVariant;
  /** Header size */
  size?: HeaderSize;
  /** Whether header has backdrop blur */
  blur?: boolean;
  /** Whether header has border */
  bordered?: boolean;
  /** Custom background color */
  backgroundColor?: string;
  /** Custom z-index */
  zIndex?: number;
  /** Children elements */
  children: React.ReactNode;
}

// Get variant styles
const getVariantStyles = (variant: HeaderVariant) => {
  switch (variant) {
    case 'sticky':
      return css`
        position: sticky;
        top: 0;
        z-index: ${({ theme }) => theme.zIndex.sticky};
      `;
    
    case 'floating':
      return css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: ${({ theme }) => theme.zIndex.modal};
      `;
    
    case 'default':
    default:
      return css`
        position: relative;
      `;
  }
};

// Get size styles
const getSizeStyles = (size: HeaderSize) => {
  switch (size) {
    case 'sm':
      return css`
        padding: ${({ theme }) => theme.spacing.semantic.inset.sm} 0;
        min-height: 56px;
      `;
    
    case 'lg':
      return css`
        padding: ${({ theme }) => theme.spacing.semantic.inset.lg} 0;
        min-height: 80px;
      `;
    
    case 'md':
    default:
      return css`
        padding: ${({ theme }) => theme.spacing.semantic.inset.md} 0;
        min-height: 64px;
      `;
  }
};

// Styled header component
const StyledHeader = styled.header<HeaderProps>`
  width: 100%;
  display: flex;
  align-items: center;
  background-color: ${({ backgroundColor, theme }) => 
    backgroundColor || theme.colors.surface.primary
  };
  
  ${({ variant = 'default' }) => getVariantStyles(variant)}
  ${({ size = 'md' }) => getSizeStyles(size)}
  
  ${({ blur }) => blur && css`
    backdrop-filter: blur(10px);
    background-color: ${({ theme }) => theme.colors.surface.overlay};
  `}
  
  ${({ bordered, theme }) => bordered && css`
    border-bottom: 1px solid ${theme.colors.border.subtle};
  `}
  
  ${({ zIndex }) => zIndex && css`
    z-index: ${zIndex};
  `}
`;

// Header content wrapper
const HeaderContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.spacing.layout.container.paddingX.mobile};
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.values.md}px) {
    padding: 0 ${({ theme }) => theme.spacing.layout.container.paddingX.tablet};
  }
  
  @media (min-width: ${({ theme }) => theme.breakpoints.values.lg}px) {
    padding: 0 ${({ theme }) => theme.spacing.layout.container.paddingX.desktop};
  }
`;

// Header component
export const Header = forwardRef<HTMLElement, HeaderProps>(
  ({ children, ...props }, ref) => {
    return (
      <StyledHeader ref={ref} {...props}>
        <HeaderContent>
          {children}
        </HeaderContent>
      </StyledHeader>
    );
  }
);

Header.displayName = 'Header';

export default Header;
